import React, { useState } from "react";
import { motion } from "framer-motion";

export default function PhysicsMagneticExperiment({ onComplete }) {
  const [mode, setMode] = useState("neutral");
  const [mag1, setMag1] = useState({ poleLeft: "N", poleRight: "S" });
  const [mag2, setMag2] = useState({ poleLeft: "N", poleRight: "S" });

  const magnetState = getMagneticMode(mag1, mag2, mode);

  // compass angle based on actual poles
  const compassAngle =
    magnetState === "attract"
      ? 0
      : magnetState === "repel"
      ? 180
      : 90;

  return (
    <div className="flex flex-col items-center mt-10 px-4">
      <h2 className="text-3xl font-bold text-blue-800 mb-2 text-center">
        Magnetic Attraction & Repulsion
      </h2>

      <p className="text-gray-600 mb-4 text-center max-w-xl">
        Flip the poles to create LIKE or UNLIKE pole combinations.
      </p>

      {/* MAIN LAB AREA */}
      <div
        className="relative bg-white border rounded shadow"
        style={{ width: "100%", maxWidth: "900px", height: "380px" }}
      >
        {/* FIELD LINES */}
        <MagneticFieldLines mode={magnetState} />

        {/* MAGNET 1 */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 left-[120px]"
          animate={{
            x: magnetState === "attract" ? 30 : magnetState === "repel" ? -30 : 0,
          }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
        >
          <BarMagnet poleLeft={mag1.poleLeft} poleRight={mag1.poleRight} />
          <FlipButton onClick={() => flipMagnet(mag1, setMag1)} />
        </motion.div>

        {/* MAGNET 2 */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 right-[120px]"
          animate={{
            x: magnetState === "attract" ? -30 : magnetState === "repel" ? 30 : 0,
          }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
        >
          <BarMagnet poleLeft={mag2.poleLeft} poleRight={mag2.poleRight} />
          <FlipButton onClick={() => flipMagnet(mag2, setMag2)} />
        </motion.div>

        {/* COMPASS GRID */}
        <CompassGrid angle={compassAngle} />
      </div>

      {/* MAIN BUTTONS */}
      <div className="mt-6 w-full max-w-[600px] p-5 bg-gray-50 rounded shadow text-center">
        <p className="text-gray-700 text-sm mb-3">
          System mode: <b>{magnetState.toUpperCase()}</b>
        </p>

        <div className="flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => {
              setMode("auto"); // auto detect based on poles
              onComplete && onComplete();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Automatic Mode
          </button>

          <button
            onClick={() => {
              setMode("neutral");
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------
   MAGNETIC PHYSICS LOGIC
---------------------------------------------------------------- */

function flipMagnet(mag, setter) {
  setter({
    poleLeft: mag.poleRight,
    poleRight: mag.poleLeft,
  });
}

function getMagneticMode(m1, m2, manualMode) {
  if (manualMode === "neutral") return "neutral";

  // AUTO mode: detect based on actual poles
  if (m1.poleRight === m2.poleLeft) return "repel";
  return "attract";
}

/* --------------------------------------------------------------
   FIELD LINES (Physics Style)
---------------------------------------------------------------- */

function MagneticFieldLines({ mode }) {
  if (mode === "neutral") return null;

  const attract = mode === "attract";
  const repel = mode === "repel";

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none">
      {[0, 40, 80].map((offset, i) => (
        <path
          key={i}
          d={`M180,${180 - offset} C300,${attract ? 80 - offset : 120 - offset} 420,${
            attract ? 80 - offset : 120 - offset
          } 540,${180 - offset}`}
          stroke={attract ? "green" : "red"}
          strokeWidth="4"
          fill="none"
          opacity="0.8"
        />
      ))}

      {[0, 40, 80].map((offset, i) => (
        <path
          key={i + "bot"}
          d={`M180,${180 + offset} C300,${attract ? 280 + offset : 240 + offset} 420,${
            attract ? 280 + offset : 240 + offset
          } 540,${180 + offset}`}
          stroke={attract ? "green" : "red"}
          strokeWidth="4"
          fill="none"
          opacity="0.8"
        />
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------
   MAGNET DRAWING
---------------------------------------------------------------- */

function BarMagnet({ poleLeft, poleRight }) {
  return (
    <div className="w-40 h-14 flex rounded-md shadow border overflow-hidden">
      <div className="w-1/2 bg-red-600 text-white flex items-center justify-center font-bold text-lg">
        {poleLeft}
      </div>
      <div className="w-1/2 bg-blue-600 text-white flex items-center justify-center font-bold text-lg">
        {poleRight}
      </div>
    </div>
  );
}

/* Flip button */
function FlipButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="mt-2 px-3 py-1 rounded bg-gray-200 text-gray-800 text-xs shadow hover:bg-gray-300"
    >
      Flip Poles
    </button>
  );
}

/* --------------------------------------------------------------
   COMPASS GRID
---------------------------------------------------------------- */

function CompassGrid({ angle }) {
  const positions = [
    [260, 120],
    [340, 120],
    [420, 120],
    [260, 240],
    [340, 240],
    [420, 240],
  ];

  return (
    <>
      {positions.map(([x, y], i) => (
        <Compass key={i} x={x} y={y} angle={angle} />
      ))}
    </>
  );
}

function Compass({ x, y, angle }) {
  return (
    <motion.div
      className="absolute"
      style={{ left: x - 18, top: y - 18 }}
    >
      <div className="w-9 h-9 rounded-full border-2 border-gray-700 bg-white flex items-center justify-center relative">
        <motion.div
          animate={{ rotate: angle }}
          transition={{ type: "spring", stiffness: 150, damping: 15 }}
          className="w-1 h-7 bg-red-500 absolute top-0.5 origin-bottom rounded"
        />
        <motion.div
          animate={{ rotate: angle + 180 }}
          transition={{ type: "spring", stiffness: 150, damping: 15 }}
          className="w-1 h-7 bg-blue-500 absolute bottom-0.5 origin-top rounded"
        />
      </div>
    </motion.div>
  );
}
